/**
 * Feedback contract constants — MIRROR of `web-services/packages/trace-contract`
 * (@web-services/trace-contract), the single source of truth (spec-traces-and-feedback §4.0(a),
 * D22). The widget shares no workspace/package link with web-services, so the values are copied
 * verbatim; `feedbackContract.test.js` loads the server package and fails on any drift.
 *
 * All eleven §4.0(a) names are copied, not a subset: a mirror that copies only what the widget
 * reads today drifts silently the first time the widget grows a feature. Never use a withdrawn
 * spelling (`FEEDBACK_CHIPS`, `SIGNAL_SOURCES`, `SIGNAL_CATEGORIES`, `CATEGORY`,
 * `FEEDBACK_FREE_TEXT_MAX`).
 *
 * `CLUSTER` mirrors the cluster-id SHAPE and constants only; the hash formulas are server-side
 * (sub-spec 07b) — the widget never computes a cluster.
 */

const freeze = (value) => {
	Object.values(value).forEach((v) => { if (v && typeof v === "object") freeze(v); });
	return Object.freeze(value);
};

export const CHIPS = freeze({
	POSITIVE: ["accurate", "relevant", "clear", "complete", "well_sourced", "friendly_tone", "other"],
	NEGATIVE: ["inaccurate", "irrelevant", "confusing", "incomplete", "outdated", "wrong_tone", "other"],
});

export const CHIP_KEYS_BY_RATING = Object.freeze({ up: CHIPS.POSITIVE, down: CHIPS.NEGATIVE });

/** The one i18n key formula for a chip / category label. */
export const CATEGORY_LABEL_KEY = (category) => `FEEDBACK.CHIP.${String(category).toUpperCase()}`;

export const SOURCES = Object.freeze(["customer", "operator", "builder", "system"]);

export const SURFACES = Object.freeze(["chat", "voice", "copilot", "test"]);

export const CATEGORIES = Object.freeze([
	"accurate", "relevant", "clear", "complete", "well_sourced", "friendly_tone", "other",
	"inaccurate", "irrelevant", "confusing", "incomplete", "outdated", "wrong_tone",
	"rating_up", "rating_down",
	"no_data", "policy_violation", "injection_block", "tool_result_dropped",
	"suggestion_shown", "suggestion_accepted", "suggestion_not_accepted", "suggestion_accepted_down",
]);

export const CAUSE_ACTIONS = freeze({
	connector: ["repair_connector", "review_tool"],
	policy: ["review_policy", "edit_guidance"],
	knowledge: ["add_verified_answer", "edit_guidance"],
	routing: ["assign_skill", "edit_instructions"],
	style: ["edit_guidance", "edit_instructions"],
	none: ["add_verified_answer"],
});

export const ACTIONS = Object.freeze([
	"add_verified_answer", "assign_skill", "edit_instructions", "edit_guidance",
	"repair_connector", "review_tool", "review_policy", "dismiss",
]);

export const LINK_ONLY = Object.freeze(["view_conversation"]);

/** The widget enforces the same free-text cap the server does. A constant, not configuration. */
export const TEXT = Object.freeze({ FREE_TEXT_MAX: 500, LIST_CLAMP: 240 });

export const CLUSTER = Object.freeze({
	BUCKET_BITS: 16,
	HYPERPLANE_SEED: "trace-cluster-v1",
	id: (agentId, category, bucket) => `${agentId}:${category}:${bucket}`,
	parseId: (id) => {
		const [agentId, category, bucket] = String(id).split(":");
		return agentId && category && bucket ? { agentId, category, bucket } : null;
	},
});
